import { useEffect } from 'react';

import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { Stepper } from '@/components/Stepper';
import { JOURNEY_STEP } from '@/components/stepper.steps';
import { usePayCardOnFile } from '@/features/paycard/hooks/usePayCardOnFile';
import { useTranslation } from '@/i18n/useTranslation';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

/**
 * The confirmation between applying and the set-up steps [KAP-7 § submitted].
 *
 * **It confirms a submission, never a placement.** The screen is called `placement` in the prototype and
 * the name is misleading: the kiosk stops at the submission [decision 0097], a recruiter decides the rest,
 * and the copy is careful about it — *"A recruiter reviews it next, and we'll confirm by email once your
 * assignment is set"*. Nothing here creates an assignment, and nothing should.
 *
 * No Back: the submission behind this screen already exists in Bullhorn, and offering a way back to the fit
 * questions would invite a second one.
 */
export function SubmittedScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const goTo = useKioskSession((state) => state.goTo);
  const job = useKioskSession((state) => state.selectedJob);
  const submission = useKioskSession((state) => state.submission);
  const submissionFailed = useKioskSession((state) => state.submissionFailed);
  const payCardOnFile = usePayCardOnFile();

  /*
   * ⚠️ **Never claim an application that does not exist.**
   *
   * This screen says "Your interest in the job has been submitted." with a green tick. Reaching it with no submission means the
   * profile save failed, so the job detail skipped the submission too — there is no candidate record, no
   * JobSubmission and nothing on any recruiter's list. Telling that person a recruiter reviews their
   * application next sends them home to wait for a call nobody will make.
   *
   * So this shows the representative screen instead, which is honest and is copy that already exists.
   *
   * **The queue entry is raised THERE, by the screen that makes the promise, not here.** It used to be
   * written on this line as well, which made this a second place that could enqueue a candidate without
   * anyone asking — and on the routing path that reaches `finish` some other way, the only place. One
   * promise, one entry, and `summonRepresentative` keeps it to one row per candidate either way.
   *
   * ⭐ **When a write was sent and failed, it is the submission-failed screen instead** [KPW-1c; E4]. It
   * still ends at the same hand-off. The flag is the LATEST write's: `setSubmission` clears it, so E4 never
   * shows after a write the api accepted, and an earlier submission in this session cannot hide a later
   * failure behind this screen's "submitted" for the job that failed.
   */
  useEffect(() => {
    if (submissionFailed) goTo(SCREEN.submissionFailed);
    else if (submission === undefined) goTo(SCREEN.finish);
  }, [submission, submissionFailed, goTo]);

  if (submission === undefined || submissionFailed) return null;

  return (
    <KioskScreen>
      <KioskCard band={<Stepper current={JOURNEY_STEP.submitted} />} rhythm="banded">
        {/*
          No success tick and no status badge, and the column reads left.
          
          The live frame (12021:42080 and its two siblings) draws the rail, then the heading, the body and a
          callout — no 56px tick circle, no header row at all. The tick and the centred column were ours; the
          rail already says the step is done, and centring made this the only form-flow screen that did.
        */}
        {/*
          `sm:pt-8` is the other half of the `pb-8` that was already here, and its absence is what the
          Operator kept reporting as *"the text should sit further down"*: the frame's `Content` node is
          `pad[T32 R0 B32 L0]` at 1440 and 820, so the heading opens 32px below the journey rail, and the
          build had 32 at the bottom and nothing at the top — the rail sat hard against the heading.

          No `pt` below `sm`, deliberately: the 375 frame draws `Content` at `pad[T0 B32]` and puts its 24px
          between the card's slots instead, which is `banded`'s job and not this column's. Setting both
          would add, the way `KioskScreen`'s gap and `KioskCard`'s top margin once did.
        */}
        <div className="flex w-full flex-col items-start gap-6 pb-8 sm:pt-8">
          {/* The frame groups the title and its body as one 448x96 block — 28 + 8 + 60. */}
          <div className="flex w-full flex-col items-start gap-2">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
              ref={heading}
              tabIndex={-1}
            >
              {t('submitted.heading')}
            </h1>
            <p className="w-full text-sm leading-5 text-[var(--muted-foreground)]">
              {t('submitted.body', { job: job?.title ?? '' })}
            </p>
          </div>
          {/* The frame sets this as a Callout (448x72), not a bold line. */}
          <Callout>{t('submitted.oneMore')}</Callout>
          {/*
            IN THE COLUMN, not in the card's footer slot — this screen has no `Actions` band at all.

            The frame draws `Next` (86x52) as `Content`'s third child, 24px under the callout and on the
            column's own left edge: x=496 of the 448 column at 1440, x=186 at 820, x=24 at 375. The build
            passed it as `footer`, which is a full-card-width strip with its own 16px of padding, so the
            button came out `dy+40` at 1440, `dx-28` at 820 and `dx-8` at 375 — 24px of it from the footer's
            padding stacking on this column's `pb-8`, the rest from the strip being wider than the column.

            The 2026-09-02 pass found the left edge and kept the strip, which is why its comment had to
            explain why one screen's footer aligns differently from Check-in's and Finish's. It does not:
            it is not a footer. With the button inside, `Content` measures 448x332 at 1440 and 820 and
            375x300 at 375 — the frame's own numbers, exactly.
          */}
          {/*
            To `payCardWhy`, not straight to the scan. The pay-card step opened cold — it asks for a
            barcode a first-time candidate is not holding, and nothing told them the packets are in a box
            beside the kiosk. `11 · Why a pay card` is drawn between these two screens in the file's
            new-user flow and wired between them in the prototype; the arrival-menu errand still bypasses
            it [see `SCREEN.payCardWhy`].

            ⭐ **Only without a card on file** [KPC-1f; decision 0470]: with one, Next goes straight to
            `10a · Your next steps` — no pay-card screen and no question about a card. The record decides,
            never new-or-returning, and any doubt shows the step [`usePayCardOnFile`].
          */}
          <Button onClick={() => goTo(payCardOnFile ? SCREEN.finish : SCREEN.payCardWhy)}>
            {t('submitted.next')}
          </Button>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
